/**
 * Review-edit trip: leave review, edit a real schema page, return.
 * Layout (accordion vs flat) lives in flatReview.js.
 *
 * Redux holds trip meta only. Cancel baseline stays module-local (PII).
 */

import { cloneDeep } from 'lodash-es';
import recordEvent from 'platform/monitoring/record-event';
import get from '../../../../utilities/data/get';
import { getActiveExpandedPages } from '../helpers';
import { isFlatReviewEnabled } from './flatReview';

export const REVIEW_EDIT_EVENTS = {
  TRIP_START: 'review-edit-trip-start',
  UPDATE: 'review-edit-update',
  CANCEL: 'review-edit-cancel',
  DEPENDS_WALK: 'review-edit-depends-walk',
};

/**
 * Forms-system review-edit events. trackingPrefix already has a trailing
 * hyphen (e.g. ss-4138-). Event name only — no page keys or form data.
 *
 * @param {string} [trackingPrefix]
 * @param {string} [action]
 */
export function recordReviewEditEvent(trackingPrefix, action) {
  if (!trackingPrefix || !action) {
    return;
  }
  recordEvent({ event: `${trackingPrefix}${action}` });
}

export const REVIEW_EDIT_QUERY = 'review';
export const REVIEW_EDIT_RETURN_FOCUS_KEY = 'reviewEditReturnFocus';
export const REVIEW_EDIT_SUCCESS_KEY = 'reviewEditSuccess';
export const REVIEW_EDIT_CHANGED_KEY = 'reviewEditChangedChapters';

export const START_REVIEW_EDIT_TRIP = 'START_REVIEW_EDIT_TRIP';
export const FINISH_REVIEW_EDIT_TRIP = 'FINISH_REVIEW_EDIT_TRIP';
export const REMEMBER_REVIEW_EDIT_PAGES = 'REMEMBER_REVIEW_EDIT_PAGES';
export const REMEMBER_REVIEW_EDIT_PAGE_KEY = 'REMEMBER_REVIEW_EDIT_PAGE_KEY';

export const REVIEW_EDIT_DEPENDS_WARNING_TITLE =
  'Changing your answer may lead to a new set of questions';
export const REVIEW_EDIT_DEPENDS_WARNING_BODY =
  'If you change your answer, you may need to provide more information before you can submit.';

export const initialReviewEditTrip = {
  active: false,
  formId: '',
  originPageKey: '',
  returnPath: '',
  pageKeys: [],
  seenPageKeys: [],
};

/** Cancel snapshots. Not Redux and not sessionStorage — form data is PII. */
let reviewEditBaselines = Object.create(null);

function tripId(formId) {
  return formId || 'form';
}

function uniqueKeys(keys = []) {
  return [...new Set((keys || []).filter(Boolean))];
}

function asKeySet(keys) {
  return keys instanceof Set ? keys : new Set(keys || []);
}

export function stashReviewEditBaseline(formId, formData) {
  const id = tripId(formId);
  if (reviewEditBaselines[id]) {
    return reviewEditBaselines[id];
  }
  reviewEditBaselines[id] = cloneDeep(formData || {});
  return reviewEditBaselines[id];
}

export function peekReviewEditBaseline(formId) {
  return reviewEditBaselines[tripId(formId)];
}

export function takeReviewEditBaseline(formId) {
  const id = tripId(formId);
  const baseline = reviewEditBaselines[id];
  delete reviewEditBaselines[id];
  return baseline;
}

export function clearReviewEditBaselines() {
  reviewEditBaselines = Object.create(null);
}

export function selectReviewEditTrip(state) {
  return state?.form?.reviewEditTrip || initialReviewEditTrip;
}

export function isReviewQuery(location) {
  const review = location?.query?.[REVIEW_EDIT_QUERY];
  if (review === 'true' || review === true) {
    return true;
  }

  const search =
    location?.search ||
    (typeof window !== 'undefined' ? window.location.search : '');
  if (!search) {
    return false;
  }

  return new URLSearchParams(search).get(REVIEW_EDIT_QUERY) === 'true';
}

/**
 * True when this FormPage should use Update/Cancel instead of Continue/Back.
 *
 * @param {Object} [formConfig]
 * @param {Object} [featureToggles]
 * @param {Object} [location]
 * @returns {boolean}
 */
export function isReviewEditTrip(formConfig, featureToggles, location) {
  return (
    isFlatReviewEnabled(formConfig, featureToggles) && isReviewQuery(location)
  );
}

export function startReviewEditTrip({
  formId,
  originPageKey,
  returnPath,
  pageKeys = [],
} = {}) {
  return {
    type: START_REVIEW_EDIT_TRIP,
    formId: formId || '',
    originPageKey: originPageKey || '',
    returnPath: returnPath || '',
    pageKeys: uniqueKeys(pageKeys),
  };
}

export function finishReviewEditTrip() {
  return { type: FINISH_REVIEW_EDIT_TRIP };
}

export function rememberReviewEditPages(pageKeys = []) {
  return {
    type: REMEMBER_REVIEW_EDIT_PAGES,
    pageKeys: uniqueKeys(pageKeys),
  };
}

export function rememberReviewEditPageKey(pageKey) {
  return {
    type: REMEMBER_REVIEW_EDIT_PAGE_KEY,
    pageKey: pageKey || '',
  };
}

function normalizePath(path) {
  return (path || '').split('?')[0].replace(/\/$/, '') || '/';
}

function pageKeyOf(page) {
  return `${page?.pageKey || ''}${page?.index ?? ''}`;
}

/**
 * Path of the form's review & submit page.
 *
 * @param {Array} [pageList]
 * @param {string} [urlPrefix]
 * @returns {string}
 */
export function getReviewPagePath(pageList, urlPrefix = '') {
  const reviewPage = pageList?.find(
    page => page.pageKey === 'review-and-submit',
  );
  if (reviewPage?.path) {
    return reviewPage.path;
  }

  const prefix = urlPrefix || '';
  const joined = `${prefix}review-and-submit`.replace(/\/{2,}/g, '/');
  return joined.startsWith('/') ? joined : `/${joined}`;
}

/**
 * React Router location for returning to review after a schema edit.
 *
 * @param {Array} [pageList]
 * @param {string} [urlPrefix]
 * @param {string} [pageKey]
 * @param {{ success?: boolean, changedChapters?: string[] }} [options]
 */
export function getReviewReturnLocation(
  pageList,
  urlPrefix = '',
  pageKey = '',
  { success = false, changedChapters = [] } = {},
) {
  return {
    pathname: getReviewPagePath(pageList, urlPrefix),
    state: {
      reviewEditReturnPageKey: pageKey || '',
      reviewEditSuccess: Boolean(success),
      reviewEditChangedChapters: changedChapters,
    },
  };
}

/**
 * Path + query for opening a schema page from a review Edit link.
 *
 * @param {Object} page
 * @param {Array} [pageList]
 * @returns {string}
 */
export function getSchemaPageEditPath(page, pageList = []) {
  const listed = pageList.find(
    listedPage => listedPage.pageKey === page.pageKey,
  );
  let path = listed?.path || page.path || '';

  if (page.index != null && path.includes(':index')) {
    path = path.replace(':index', page.index);
  }

  if (path && !path.startsWith('/')) {
    path = `/${path}`;
  }

  if (!path) {
    return '';
  }

  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${REVIEW_EDIT_QUERY}=true`;
}

/**
 * React Router location for opening a schema page from review Edit.
 *
 * @param {Object} page
 * @param {Array} [pageList]
 * @returns {{ pathname: string, search: string, query: { review: string } } | null}
 */
export function getSchemaPageEditLocation(page, pageList = []) {
  const path = getSchemaPageEditPath(page, pageList);
  if (!path) {
    return null;
  }

  const [pathname, search = ''] = path.split('?');
  return {
    pathname,
    search: search ? `?${search}` : '',
    query: { [REVIEW_EDIT_QUERY]: 'true' },
  };
}

/**
 * Next schema page unlocked by this edit, or null to return to review.
 *
 * @param {Object} trip form.reviewEditTrip
 * @param {Array} pageList
 * @param {Object} formData
 * @param {string} pathname
 * @param {string} [pageKey]
 */
export function getNextReviewEditLocation(
  trip,
  pageList,
  formData,
  pathname,
  pageKey,
) {
  if (!trip || !(trip.active || trip.pageKeys?.length)) {
    return null;
  }

  const pages = getActiveExpandedPages(pageList || [], formData);
  const normalizedPath = normalizePath(pathname);
  let pageIndex = pageKey
    ? pages.findIndex(page => pageKeyOf(page) === pageKey)
    : -1;
  if (pageIndex < 0) {
    pageIndex = pages.findIndex(
      page => normalizePath(page.path) === normalizedPath,
    );
  }
  if (pageIndex < 0) {
    return null;
  }

  const originKeys = asKeySet(trip.pageKeys);
  const seen = asKeySet(trip.seenPageKeys);
  for (let i = pageIndex + 1; i < pages.length; i += 1) {
    const nextPage = pages[i];
    if (!nextPage || nextPage.pageKey === 'review-and-submit') {
      return null;
    }
    const nextKey = pageKeyOf(nextPage);
    if (!originKeys.has(nextKey) && !seen.has(nextKey)) {
      return getSchemaPageEditLocation(nextPage, pageList);
    }
  }

  return null;
}

function pageMissingRequired(page, pageState, formData) {
  const required = pageState?.schema?.required;
  if (!required?.length) {
    return false;
  }
  const data = page.showPagePerItem
    ? get([page.arrayPath, page.index], formData)
    : formData;
  return required.some(key => {
    const value = get(key, data);
    return value === undefined || value === null || value === '';
  });
}

/**
 * Chapters that gained required pages after a review edit and still need answers.
 *
 * @param {Array} pageList
 * @param {Object} formData
 * @param {Object} [trip]
 * @param {Object} [formPages]
 * @returns {string[]}
 */
export function collectReviewEditChangedChapters(
  pageList,
  formData,
  trip,
  formPages = {},
) {
  const originKeys = asKeySet(trip?.pageKeys);
  if (!originKeys.size) {
    return [];
  }
  const expanded = getActiveExpandedPages(pageList || [], formData);
  const chapters = new Set();
  expanded.forEach(page => {
    const fullKey = `${page.pageKey}${page.index ?? ''}`;
    if (originKeys.has(fullKey)) {
      return;
    }
    if (pageMissingRequired(page, formPages[page.pageKey], formData)) {
      chapters.add(page.chapterKey);
    }
  });
  return [...chapters].filter(Boolean);
}

function sessionGet(key) {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.sessionStorage.getItem(key) || '';
}

function sessionSet(key, value) {
  if (typeof window === 'undefined') {
    return;
  }
  // lgtm[js/clear-text-storage-of-sensitive-data]
  window.sessionStorage.setItem(key, value); // UI state only, not PII
}

function sessionClear(key) {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(key);
}

export function setReviewEditReturnFocus(pageKey) {
  sessionSet(REVIEW_EDIT_RETURN_FOCUS_KEY, pageKey || '');
}

export function consumeReviewEditReturnFocus() {
  const pageKey = sessionGet(REVIEW_EDIT_RETURN_FOCUS_KEY);
  sessionClear(REVIEW_EDIT_RETURN_FOCUS_KEY);
  return pageKey;
}

export function setReviewEditSuccessAlert(message) {
  sessionSet(REVIEW_EDIT_SUCCESS_KEY, message || '1');
}

export function consumeReviewEditSuccessAlert() {
  const value = sessionGet(REVIEW_EDIT_SUCCESS_KEY);
  sessionClear(REVIEW_EDIT_SUCCESS_KEY);
  return Boolean(value);
}

export function setReviewEditChangedChapters(chapterKeys = []) {
  sessionSet(REVIEW_EDIT_CHANGED_KEY, JSON.stringify(chapterKeys));
}

export function consumeReviewEditChangedChapters() {
  const raw = sessionGet(REVIEW_EDIT_CHANGED_KEY);
  sessionClear(REVIEW_EDIT_CHANGED_KEY);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}
